import type { ApiResult } from './types';
import { apiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import { formatUserRolesDisplay, type UserRoleDisplayEntry } from '../utils/userFieldValidation';
import { getAppUserId } from './activeAppUser';
import { formatDisplayDateTime } from '../utils/dateFormat';
import { buildProfilePicPayloadFields, extractProfilePictureFields } from '../utils/profilePictureRef';

/** Profile shape used by case-request wizard review/submission screens (Extranet parity). */
export type ExtranetUserProfile = {
  externalAgencyName: string;
  divisionUnit: string;
  brn: string;
  companyCode: string;
  email: string;
  displayName: string;
  contactPersonName: string;
  contactNumber: string;
  contactDesignation: string;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  active: boolean;
};

function withAppCode(applicationCode: string): { applicationCode: string } {
  return { applicationCode };
}

export type InternalUserProfileRole = UserRoleDisplayEntry & {
  roleId: number;
};

export type InternalUserProfile = {
  appUserId: string;
  /** DMS storage key (UUID) — fetch via file-management download for display. */
  profilePictureIconUrl: string;
  profilePictureIconUrlFullpath: string;
  username: string;
  userDisplayName: string;
  title: string;
  firstName: string;
  lastName: string;
  nic: string;
  categoryName: string;
  categoryCode: string;
  categoryId: number | null;
  roleId: number | null;
  roleName: string;
  userRoles: InternalUserProfileRole[];
  rolesDisplay: string;
  divisionId: number | null;
  divisionName: string;
  designation: string;
  designationId: number | null;
  serviceId: string;
  contactNumber: string;
  active: boolean;
  updatedAt: string;
  updatedByName: string;
  createdAt: string;
  createdByName: string;
};

type ProfileRoleResponse = {
  role_id?: number;
  role_name?: string | null;
  default_role?: boolean | null;
};

type ProfileInternalByIdResponse = {
  app_user_id: string;
  profile_picture_icon_url?: string | null;
  profilePictureIconUrl?: string | null;
  profile_picture_icon_url_fullpath?: string | null;
  profilePictureIconUrlFullpath?: string | null;
  profile_pic_file_ref?: string | null;
  profilePicFileRef?: string | null;
  username?: string | null;
  user_display_name?: string | null;
  title?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  nic?: string | null;
  category_name?: string | null;
  category_code?: string | null;
  category_id?: number | null;
  role_id?: number | null;
  role_name?: string | null;
  user_roles?: ProfileRoleResponse[];
  division_id?: number | null;
  division_name?: string | null;
  designation?: string | null;
  designationId?: number | null;
  service_id?: string | null;
  contact_number?: string | null;
  active?: boolean | null;
  updated_at?: string | null;
  updated_by_name?: string | null;
  created_at?: string | null;
  created_by_name?: string | null;
};

function normalizeProfileUserRoles(
  raw: ProfileRoleResponse[] | undefined,
  fallbackRoleId?: number | null,
  fallbackRoleName?: string | null
): InternalUserProfileRole[] {
  const userRoles: InternalUserProfileRole[] = (raw ?? [])
    .map((x) => ({
      roleId: typeof x.role_id === 'number' ? x.role_id : 0,
      roleName: (x.role_name ?? '').trim(),
      defaultRole: Boolean(x.default_role),
    }))
    .filter((x) => x.roleId > 0 && x.roleName.length > 0);

  if (!userRoles.length && typeof fallbackRoleId === 'number' && fallbackRoleId > 0) {
    userRoles.push({
      roleId: fallbackRoleId,
      roleName: (fallbackRoleName ?? '').trim() || 'Assigned Officer',
      defaultRole: true,
    });
  }
  if (!userRoles.length && (fallbackRoleName ?? '').trim()) {
    userRoles.push({
      roleId: typeof fallbackRoleId === 'number' ? fallbackRoleId : 0,
      roleName: (fallbackRoleName ?? '').trim(),
      defaultRole: true,
    });
  }

  if (userRoles.length && !userRoles.some((x) => x.defaultRole)) {
    return userRoles.map((x, i) => ({ ...x, defaultRole: i === 0 }));
  }
  return userRoles;
}

function mapInternalProfile(r: ProfileInternalByIdResponse): InternalUserProfile {
  const userRoles = normalizeProfileUserRoles(r.user_roles, r.role_id, r.role_name);
  const defaultEntry = userRoles.find((x) => x.defaultRole) ?? userRoles[0];
  const roleName = (defaultEntry?.roleName ?? r.role_name ?? '').trim();
  const roleId = defaultEntry?.roleId ?? (typeof r.role_id === 'number' ? r.role_id : null);

  const { profilePictureIconUrl, profilePictureIconUrlFullpath } = extractProfilePictureFields(r);

  return {
    appUserId: r.app_user_id,
    profilePictureIconUrl,
    profilePictureIconUrlFullpath,
    username: (r.username ?? '').trim(),
    userDisplayName: (r.user_display_name ?? '').trim(),
    title: (r.title ?? '').trim(),
    firstName: (r.first_name ?? '').trim(),
    lastName: (r.last_name ?? '').trim(),
    nic: (r.nic ?? '').trim(),
    categoryName: (r.category_name ?? '').trim(),
    categoryCode: (r.category_code ?? '').trim(),
    categoryId: typeof r.category_id === 'number' ? r.category_id : null,
    roleId,
    roleName,
    userRoles,
    rolesDisplay: formatUserRolesDisplay(userRoles),
    divisionId: typeof r.division_id === 'number' ? r.division_id : null,
    divisionName: (r.division_name ?? '').trim(),
    designation: (r.designation ?? '').trim(),
    designationId: typeof r.designationId === 'number' ? r.designationId : null,
    serviceId: (r.service_id ?? '').trim(),
    contactNumber: (r.contact_number ?? '').trim(),
    active: Boolean(r.active),
    updatedAt: (r.updated_at ?? '').trim(),
    updatedByName: (r.updated_by_name ?? '').trim(),
    createdAt: (r.created_at ?? '').trim(),
    createdByName: (r.created_by_name ?? '').trim(),
  };
}

export async function getInternalUserProfileById(appUserId: string): Promise<ApiResult<InternalUserProfile>> {
  const cleaned = appUserId.trim();
  if (!cleaned) return { success: false, message: 'Missing app user id.' };

  const res = await apiRequest<ProfileInternalByIdResponse>(`/v1/user/app-user/profile-internal-by-id?appUserId=${encodeURIComponent(cleaned)}`, {
    method: 'GET',
    headers: withAppCode(import.meta.env.VITE_APP_CODE_PROFILE_INTERNAL_BY_ID ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: mapInternalProfile(res.data) };
}

/** SwitchableUserRoleResponseDto(id, name, defaultRole, orderIndex, description). */
type SwitchRoleResponseRow = {
  id?: number;
  role_id?: number;
  roleId?: number;
  name?: string | null;
  role_name?: string | null;
  roleName?: string | null;
  default_role?: boolean | null;
  defaultRole?: boolean | null;
};

/**
 * GET /v1/user/user-role/switch/roles — switchable roles for the logged-in user.
 * The backend resolves roles via `userId` (current default role is excluded) so the
 * active app-user id is passed explicitly. Populates the header "Switch Role" selector.
 */
export async function getSwitchRoles(): Promise<ApiResult<InternalUserProfileRole[]>> {
  const userId = getAppUserId();
  if (!userId) {
    return { success: false, message: 'Active user session is missing. Please sign in again.' };
  }

  const res = await apiRequest<SwitchRoleResponseRow[]>(`/v1/user/user-role/switch/roles?userId=${encodeURIComponent(userId)}`, {
    method: 'GET',
  });
  if (!res.ok) return { success: false, message: res.message };

  const rows = Array.isArray(res.data) ? res.data : [];
  const roles: InternalUserProfileRole[] = rows
    .map((x) => ({
      roleId: typeof x.id === 'number' ? x.id : typeof x.role_id === 'number' ? x.role_id : typeof x.roleId === 'number' ? x.roleId : 0,
      roleName: ((x.name ?? x.role_name ?? x.roleName) ?? '').trim(),
      defaultRole: Boolean(x.default_role ?? x.defaultRole),
    }))
    .filter((x) => x.roleId > 0 && x.roleName.length > 0);

  return { success: true, data: roles };
}

type SwitchUserLoginRequest = { data: { new_role_id: number } };
type SwitchUserLoginResponse = { status?: string; response_result?: unknown; message?: string };

/** CU-AUC-7 — PUT /v1/user/app-user/switch/id/{id}; switch the active user's current role. */
export async function switchUserRole(newRoleId: number): Promise<ApiResult<void>> {
  const userId = getAppUserId();
  if (!userId) {
    return { success: false, message: 'Active user session is missing. Please sign in again.' };
  }
  if (!Number.isFinite(newRoleId) || newRoleId <= 0) {
    return { success: false, message: 'Please select a valid role.' };
  }

  const body: SwitchUserLoginRequest = { data: { new_role_id: newRoleId } };
  const res = await apiRequest<SwitchUserLoginResponse>(`/v1/user/app-user/switch/id/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    body,
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) {
    return { success: false, message: dataPayloadErrorMessage(res.data) || 'Role switch failed.' };
  }

  return { success: true, data: undefined };
}

export type UpdateInternalProfileInput = {
  nic: string;
  serviceId: string;
  contactNumber: string;
  profilePicFileRef: string;
  profilePicFileChanged: boolean;
  existingProfilePicRef?: string | null;
};

type UpdateInternalProfileRequest = {
  data: {
    nic: string;
    service_id: string | null;
    contact_number: string | null;
    profile_pic_file_ref: string | null;
    profile_pic_file_changed: boolean;
  };
};

type UpdateInternalProfileResponse = { status?: string; response_result?: unknown; message?: string };

export async function updateInternalUserProfile(input: UpdateInternalProfileInput): Promise<ApiResult<void>> {
  const { profile_pic_file_changed, profile_pic_file_ref } = buildProfilePicPayloadFields({
    profilePicFileChanged: input.profilePicFileChanged,
    profilePicFileRef: input.profilePicFileRef,
    existingProfilePicRef: input.existingProfilePicRef,
  });
  const serviceId = input.serviceId.trim();
  const contactNumber = input.contactNumber.trim();
  const body: UpdateInternalProfileRequest = {
    data: {
      nic: input.nic,
      service_id: serviceId ? serviceId : null,
      contact_number: contactNumber ? contactNumber : null,
      profile_pic_file_ref,
      profile_pic_file_changed,
    },
  };

  const res = await apiRequest<UpdateInternalProfileResponse>(`/v1/user/app-user/update-internal-profile`, {
    method: 'PUT',
    body,
    headers: withAppCode(import.meta.env.VITE_APP_CODE_UPDATE_INTERNAL_PROFILE ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) {
    return { success: false, message: dataPayloadErrorMessage(res.data) || 'Profile update failed.' };
  }

  return { success: true, data: undefined };
}

function mapInternalToWizardProfile(p: InternalUserProfile): ExtranetUserProfile {
  const contactPersonName =
    p.userDisplayName.trim() ||
    [p.firstName, p.lastName].filter(Boolean).join(' ').trim() ||
    p.username;
  return {
    externalAgencyName: p.divisionName || p.categoryName,
    divisionUnit: p.divisionName,
    brn: '',
    companyCode: p.categoryCode,
    email: p.username,
    displayName: contactPersonName,
    contactPersonName,
    contactNumber: p.contactNumber,
    contactDesignation: p.designation,
    lastUpdatedAt: p.updatedAt ? formatDisplayDateTime(new Date(p.updatedAt)) : '',
    lastUpdatedBy: p.updatedByName,
    active: p.active,
  };
}

/** Wizard parity — load current intranet user profile for submission/delivery sections. */
export async function getProfile(): Promise<ApiResult<ExtranetUserProfile>> {
  const appUserId = getAppUserId();
  if (!appUserId) {
    return { success: false, message: 'Active user session is missing. Please sign in again.' };
  }
  const res = await getInternalUserProfileById(appUserId);
  if (!res.success) return res;
  return { success: true, data: mapInternalToWizardProfile(res.data) };
}
